import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledButton,
  StyledContainer,
  StyledTableCell,
} from "../../CSS/Styles";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  fetchUsers,
  saveUser,
} from "../../../redux/slices/orgAdmin/usersSlice";

const Users = () => {
  const features = useSelector((state) => state.app.features);
  const dispatch = useDispatch();
  const { usersList } = useSelector((state) => state.users);
  const { companyId } = useSelector((state) => state.app);
  const [openForm, setOpenForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    firstName: "",
    email: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchUsers({ companyId }));
  }, [dispatch, companyId]);

  const validate = () => {
    let tempErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!newUser.username) tempErrors.username = "Username is required.";
    if (!newUser.password) tempErrors.password = "Password is required.";
    if (!newUser.firstName) tempErrors.firstName = "First name is required.";

    if (!newUser.email) {
      tempErrors.email = "Email is required.";
    } else if (!emailRegex.test(newUser.email)) {
      tempErrors.email = "Invalid email format.";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleOpenForm = () => {
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setNewUser({
      username: "",
      password: "",
      firstName: "",
      email: "",
    });
    setErrors({});
  };

  const handleSaveUser = () => {
    if (validate()) {
      const payload = {
        username: newUser.username,
        password: newUser.password,
        firstName: newUser.firstName,
        email: newUser.email,
        companyId,
      };
      dispatch(saveUser(payload)).then(() => {
        dispatch(fetchUsers({ companyId }));
      });
      setOpenForm(false);
    }
  };

  const isFormValid =
    newUser.username && newUser.password && newUser.firstName && newUser.email;

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!openForm && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Users
            </Typography>
            <StyledButton
              sx={{ marginBottom: 2, fontSize: "14px", padding: "8px 16px" }}
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenForm}
            >
              Add User
            </StyledButton>
          </>
        )}
        {!openForm ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Username</StyledTableCell>
                  <StyledTableCell>First Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersList.map((user) => (
                  <TableRow key={user?.id}>
                    <TableCell>{user?.username}</TableCell>
                    <TableCell>{user?.firstName}</TableCell>
                    <TableCell>{user?.email}</TableCell>
                    <TableCell>
                      <StyledButton variant="outlined">Edit</StyledButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={() => setOpenForm(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">User</Typography>
            </Box>
            <Box>
              <TextField
                label="Username"
                name="username"
                value={newUser.username}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
                error={!!errors.username}
                helperText={errors.username}
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={newUser.password}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
                error={!!errors.password}
                helperText={errors.password}
              />
              <TextField
                label="Name"
                name="firstName"
                value={newUser.firstName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
              <TextField
                label="Email"
                name="email"
                value={newUser.email}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
                error={!!errors.email}
                helperText={errors.email}
              />
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={handleCloseForm}
                  sx={{ marginRight: 2 }}
                  color="error"
                  variant="contained"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveUser}
                  color="primary"
                  variant="contained"
                  disabled={!isFormValid}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </StyledContainer>
  );
};

export default Users;
